/**
 * useLiveAudio Hook
 *
 * Manages bidirectional audio streaming via WebSocket.
 * Connects to the backend's /ws/live-audio endpoint which bridges to Gemini Live API.
 */

import { useCallback, useRef, useState } from 'react';
import { AudioRecorder } from '../audio/AudioRecorder';
import { AudioPlayer } from '../audio/AudioPlayer';
import { API_BASE_URL } from '../constants';

export interface LiveAudioCallbacks {
    onTranscript: (text: string, role: 'user' | 'model') => void;
    onSearchResults: (results: { products: unknown[]; webpages: unknown[] }) => void;
    onError: (message: string) => void;
    onSessionEnd: () => void;
}

function buildWsUrl(): string {
    // Derive WebSocket URL from API base URL
    // https://domain/api/v1 → wss://domain/ws/live-audio
    // http://localhost:8080/api/v1 → ws://localhost:8080/ws/live-audio
    const url = new URL(API_BASE_URL);
    const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${url.host}/ws/live-audio`;
}

export function useLiveAudio(callbacks: LiveAudioCallbacks) {
    const [isLive, setIsLive] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [isAwaitingPermission, setIsAwaitingPermission] = useState(false);

    const wsRef = useRef<WebSocket | null>(null);
    const recorderRef = useRef<AudioRecorder | null>(null);
    const playerRef = useRef<AudioPlayer | null>(null);
    const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const callbacksRef = useRef(callbacks);
    callbacksRef.current = callbacks;

    const startSession = useCallback(async (sessionId: string, options?: { swissGerman?: boolean }) => {
        if (wsRef.current) return;
        setIsConnecting(true);

        try {
            const wsUrl = buildWsUrl();
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = () => {
                ws.send(JSON.stringify({ type: 'start', sessionId, swissGerman: !!options?.swissGerman }));
            };

            ws.onmessage = (event) => {
                let msg: any;
                try {
                    msg = JSON.parse(event.data);
                } catch {
                    return;
                }

                switch (msg.type) {
                    case 'setup_complete':
                        // Session ready — start recording
                        startRecording();
                        setIsConnecting(false);
                        setIsLive(true);
                        break;

                    case 'audio':
                        playerRef.current?.playChunk(msg.data);
                        break;

                    case 'transcript':
                        callbacksRef.current.onTranscript(msg.text, msg.role);
                        break;

                    case 'search_results':
                        callbacksRef.current.onSearchResults({
                            products: msg.products || [],
                            webpages: msg.webpages || [],
                        });
                        break;

                    case 'error':
                        console.error('[LiveAudio] Server error:', msg.message);
                        callbacksRef.current.onError(msg.message);
                        break;

                    case 'closed':
                        cleanup();
                        callbacksRef.current.onSessionEnd();
                        break;
                }
            };

            ws.onerror = (err) => {
                console.error('[LiveAudio] WebSocket error:', err);
                callbacksRef.current.onError('Verbindungsfehler');
                cleanup();
            };

            ws.onclose = () => {
                // If wsRef is still set, this was an unexpected close (not user-initiated)
                if (wsRef.current) {
                    callbacksRef.current.onError('Verbindung unterbrochen. Bitte erneut verbinden.');
                    callbacksRef.current.onSessionEnd();
                }
                cleanup();
            };

            // Keepalive ping every 30s to prevent idle timeouts
            pingIntervalRef.current = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send(JSON.stringify({ type: 'ping' }));
                }
            }, 30000);

            // Start audio player
            const player = new AudioPlayer();
            player.start();
            playerRef.current = player;

        } catch (error) {
            console.error('[LiveAudio] Failed to start:', error);
            callbacksRef.current.onError('Mikrofon konnte nicht aktiviert werden');
            cleanup();
        }
    }, []);

    const startRecording = async () => {
        try {
            // Check if we need to prompt for permission
            let needsPrompt = true;
            try {
                const permStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
                needsPrompt = permStatus.state === 'prompt';
            } catch {
                // Permissions API not supported — assume prompt needed
            }

            if (needsPrompt) {
                setIsAwaitingPermission(true);
            }

            const recorder = new AudioRecorder();
            recorder.onData = (base64Chunk) => {
                if (wsRef.current?.readyState === WebSocket.OPEN) {
                    wsRef.current.send(JSON.stringify({ type: 'audio', data: base64Chunk }));
                }
            };
            await recorder.start();
            setIsAwaitingPermission(false);
            recorderRef.current = recorder;
        } catch (error) {
            setIsAwaitingPermission(false);
            console.error('[LiveAudio] Recorder start failed:', error);

            let errorMessage = 'Mikrofon konnte nicht aktiviert werden.';
            if (error instanceof DOMException) {
                if (error.name === 'NotFoundError') {
                    errorMessage = 'Kein Mikrofon gefunden. Bitte stelle sicher, dass ein Mikrofon angeschlossen und im System aktiviert ist.';
                } else if (error.name === 'NotAllowedError') {
                    errorMessage = 'Mikrofonzugriff wurde verweigert. Bitte erlaube den Zugriff in den Browser-Einstellungen.';
                }
            }

            callbacksRef.current.onError(errorMessage);
            stopSession();
        }
    };

    const stopSession = useCallback(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'stop' }));
        }
        cleanup();
        callbacksRef.current.onSessionEnd();
    }, []);

    const cleanup = () => {
        if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
        }

        recorderRef.current?.stop();
        recorderRef.current = null;

        playerRef.current?.stop();
        playerRef.current = null;

        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }

        setIsLive(false);
        setIsConnecting(false);
        setIsAwaitingPermission(false);
    };

    const sendDialect = useCallback((swissGerman: boolean) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'set_dialect',
                dialect: swissGerman ? 'swiss_german' : 'standard'
            }));
        }
    }, []);

    const getUserLevel = useCallback(() => recorderRef.current?.level ?? 0, []);
    const getModelLevel = useCallback(() => playerRef.current?.getLevel() ?? 0, []);

    return {
        isLive,
        isConnecting,
        isAwaitingPermission,
        startSession,
        stopSession,
        sendDialect,
        getUserLevel,
        getModelLevel,
    };
}
